import { QueryClient } from "@tanstack/react-query";
import { createTRPCClient, httpBatchStreamLink } from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import superjson from "superjson";
import { TRPCProvider } from "@/integrations/trpc/react";
import type { TRPCRouter } from "@/integrations/trpc/router";

function getUrl() {
	if (typeof window !== "undefined") {
		// Client-side: use relative URL
		return "/api/trpc";
	}
	// Server-side: use full URL for self-referencing fetch
	const siteUrl = process.env.SITE_URL ?? "http://localhost:3000";
	return `${siteUrl}/api/trpc`;
}

// Get auth token from Supabase (client-side only)
async function getAuthHeaders(): Promise<Record<string, string>> {
	if (typeof window === "undefined") {
		return {};
	}
	try {
		const { supabase } = await import("@/lib/supabase-browser");
		const { data: { session } } = await supabase.auth.getSession();
		if (session?.access_token) {
			return { Authorization: `Bearer ${session.access_token}` };
		}
	} catch {
		// Ignore errors getting session
	}
	return {};
}

export const trpcClient = createTRPCClient<TRPCRouter>({
	links: [
		httpBatchStreamLink({
			transformer: superjson,
			url: getUrl(),
			headers: getAuthHeaders,
		}),
	],
});

export function getContext() {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				refetchOnWindowFocus: false,
			},
			dehydrate: { serializeData: superjson.serialize },
			hydrate: { deserializeData: superjson.deserialize },
		},
	});

	const serverHelpers = createTRPCOptionsProxy({
		client: trpcClient,
		queryClient: queryClient,
	});
	return {
		queryClient,
		trpc: serverHelpers,
	};
}

export function Provider({
	children,
	queryClient,
}: {
	children: React.ReactNode;
	queryClient: QueryClient;
}) {
	return (
		<TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
			{children}
		</TRPCProvider>
	);
}
